import { FireConfig, IncomeStream, ManualAsset } from '../types';
import { calculateFutureValue, computeIncomeStreamAmountAtAge, LIQUID_FIRE_ASSET_TYPES, PENSION_ASSET_TYPES, RISK_LIQUID_ASSET_TYPES } from './fireMetrics';

export const DEFAULT_PRIVATE_PENSION_ACCESS_AGE = 57;
export const DEFAULT_STATE_PENSION_AGE = 67;

const PENSION_NAME_PATTERNS = [
  'pension',
  'defined benefit',
  'db ',
  'db-',
  'final salary',
  'annuity',
  'state pension',
];

export interface ClassifiedIncomeStreams {
  employmentIncome: IncomeStream[];
  guaranteedPensionIncome: IncomeStream[];
  otherIncome: IncomeStream[];
}

export interface PensionTimelineEntry {
  age: number;
  EmploymentIncome: number;
  LiquidBridgeAssets: number;
  PrivatePensionDrawdown: number;
  DbStatePension: number;
  OtherIncome: number;
  TargetSpend: number;
}

export interface BridgeStatus {
  status: 'secured' | 'gap' | 'low-liquid';
  title: string;
  message: string;
  requiredBridgePot: number;
  privatePensionGapYears: number;
  statePensionGapYears: number;
}

export const isGuaranteedPensionIncomeStream = (stream: IncomeStream): boolean => {
  const name = stream.name.toLowerCase();
  return (
    stream.type === 'Pension' ||
    stream.type === 'Annuity' ||
    PENSION_NAME_PATTERNS.some((pattern) => name.includes(pattern))
  );
};

export const classifyIncomeStreams = (streams: IncomeStream[]): ClassifiedIncomeStreams => {
  return streams.reduce<ClassifiedIncomeStreams>((groups, stream) => {
    if (isGuaranteedPensionIncomeStream(stream)) {
      groups.guaranteedPensionIncome.push(stream);
    } else if (stream.type === 'Salary' || stream.type === 'Side-Hustle' || stream.type === 'Rental') {
      groups.employmentIncome.push(stream);
    } else {
      groups.otherIncome.push(stream);
    }
    return groups;
  }, { employmentIncome: [], guaranteedPensionIncome: [], otherIncome: [] });
};

export const getProjectedRetirementExpenseAtAge = (config: Pick<FireConfig, 'annualExpenses' | 'retirementExpenses' | 'inflationRate' | 'currentAge'>, age: number): number => {
  const retirementExpensesToday = config.retirementExpenses || config.annualExpenses;
  return calculateFutureValue(retirementExpensesToday, config.inflationRate, Math.max(0, age - config.currentAge));
};

export const getLiquidManualAssetValue = (assets: ManualAsset[]): number => {
  const liquidTypes = [...LIQUID_FIRE_ASSET_TYPES, ...RISK_LIQUID_ASSET_TYPES];
  return assets
    .filter((asset) => liquidTypes.includes(asset.type))
    .reduce((sum, asset) => sum + (asset.value || 0), 0);
};

export const getPensionPotValue = (assets: ManualAsset[]): number => assets
  .filter((asset) => PENSION_ASSET_TYPES.includes(asset.type))
  .reduce((sum, asset) => sum + (asset.value || 0), 0);

export const projectPensionPotValueAtAge = (assets: ManualAsset[], currentAge: number, targetAge: number): number => assets
  .filter((asset) => PENSION_ASSET_TYPES.includes(asset.type))
  .reduce((sum, asset) => sum + calculateFutureValue(asset.value || 0, asset.growthRate || 0, Math.max(0, targetAge - currentAge)), 0);

export const computeGuaranteedIncomeToday = (streams: IncomeStream[]): number =>
  classifyIncomeStreams(streams).guaranteedPensionIncome.reduce((sum, stream) => sum + (stream.amount || 0), 0);

export const computeProjectedGuaranteedIncomeAtAge = (
  streams: IncomeStream[],
  age: number,
  currentAge: number,
  incomeGrowthRate: number
): number => classifyIncomeStreams(streams).guaranteedPensionIncome
  .filter((stream) => age >= stream.startAge && age < stream.endAge)
  .reduce((sum, stream) => sum + computeIncomeStreamAmountAtAge(stream, age, currentAge, incomeGrowthRate), 0);

export const computeBridgeStatus = ({
  config,
  totalBridgeAssets,
  privatePensionAccessAge = DEFAULT_PRIVATE_PENSION_ACCESS_AGE,
  statePensionAge = DEFAULT_STATE_PENSION_AGE,
}: {
  config: FireConfig;
  totalBridgeAssets: number;
  privatePensionAccessAge?: number;
  statePensionAge?: number;
}): BridgeStatus => {
  const { guaranteedPensionIncome } = classifyIncomeStreams(config.incomeStreams);
  const earliestGuaranteedPensionStartAge = guaranteedPensionIncome.length > 0
    ? Math.min(...guaranteedPensionIncome.map((stream) => stream.startAge))
    : null;
  const firstSecureIncomeAge = earliestGuaranteedPensionStartAge === null
    ? statePensionAge
    : Math.min(earliestGuaranteedPensionStartAge, statePensionAge);
  const privatePensionGapYears = Math.max(0, privatePensionAccessAge - config.retirementAge);
  const statePensionGapYears = Math.max(0, statePensionAge - config.retirementAge);

  let requiredBridgePot = 0;
  if (config.retirementAge < privatePensionAccessAge) {
    for (let age = config.retirementAge; age < privatePensionAccessAge; age += 1) {
      const guaranteedIncome = computeProjectedGuaranteedIncomeAtAge(config.incomeStreams, age, config.currentAge, config.incomeGrowthRate);
      requiredBridgePot += Math.max(0, getProjectedRetirementExpenseAtAge(config, age) - guaranteedIncome);
    }
  }

  if (privatePensionGapYears > 0) {
    if (totalBridgeAssets >= requiredBridgePot) {
      return {
        status: 'secured',
        title: 'Bridge Secured',
        message: `Liquid FIRE assets cover the ${privatePensionGapYears}-year bridge from retirement age ${config.retirementAge} to private pension access age ${privatePensionAccessAge}. State pension gap: ${statePensionGapYears} years.`,
        requiredBridgePot,
        privatePensionGapYears,
        statePensionGapYears,
      };
    }

    return {
      status: 'gap',
      title: 'Bridge Gap Detected',
      message: `Liquid FIRE assets are low; consider ISA/GIA bridge savings for the ${privatePensionGapYears}-year gap from retirement age ${config.retirementAge} to private pension access age ${privatePensionAccessAge}. State pension gap: ${statePensionGapYears} years.`,
      requiredBridgePot,
      privatePensionGapYears,
      statePensionGapYears,
    };
  }

  const projectedRetirementSpend = getProjectedRetirementExpenseAtAge(config, config.retirementAge);
  const lowLiquidThreshold = projectedRetirementSpend * 0.5;
  if (totalBridgeAssets < lowLiquidThreshold) {
    return {
      status: 'low-liquid',
      title: 'Low Liquid Bridge Assets',
      message: `Liquid FIRE assets are low; consider ISA/GIA bridge savings. Private pension access age is ${privatePensionAccessAge}, target retirement age is ${config.retirementAge}, and first DB/state pension income starts around age ${firstSecureIncomeAge}. State pension gap: ${statePensionGapYears} years.`,
      requiredBridgePot: 0,
      privatePensionGapYears,
      statePensionGapYears,
    };
  }

  return {
    status: 'secured',
    title: 'Bridge Flexible',
    message: `Target retirement age ${config.retirementAge} is at or after private pension access age ${privatePensionAccessAge}. State pension gap: ${statePensionGapYears} years.`,
    requiredBridgePot: 0,
    privatePensionGapYears,
    statePensionGapYears,
  };
};

export const buildPensionTimeline = ({
  config,
  liquidAssets,
  privatePensionAccessAge = DEFAULT_PRIVATE_PENSION_ACCESS_AGE,
}: {
  config: FireConfig;
  liquidAssets: number;
  privatePensionAccessAge?: number;
}): PensionTimelineEntry[] => {
  const { employmentIncome, guaranteedPensionIncome, otherIncome } = classifyIncomeStreams(config.incomeStreams);
  const endAge = Math.min(95, config.lifeExpectancy);
  const investmentReturn = config.investmentReturnRate / 100;

  let currentLiquid = liquidAssets + getLiquidManualAssetValue(config.manualAssets);
  let currentPensionPot = getPensionPotValue(config.manualAssets);

  const data: PensionTimelineEntry[] = [];

  for (let age = config.currentAge; age <= endAge; age += 1) {
    const isRetired = age >= config.retirementAge;
    const canAccessPrivate = age >= privatePensionAccessAge;
    const targetSpend = isRetired ? getProjectedRetirementExpenseAtAge(config, age) : 0;

    const employment = employmentIncome
      .filter((stream) => age >= stream.startAge && age < stream.endAge)
      .reduce((sum, stream) => sum + computeIncomeStreamAmountAtAge(stream, age, config.currentAge, config.incomeGrowthRate), 0);
    const guaranteed = guaranteedPensionIncome
      .filter((stream) => age >= stream.startAge && age < stream.endAge)
      .reduce((sum, stream) => sum + computeIncomeStreamAmountAtAge(stream, age, config.currentAge, config.incomeGrowthRate), 0);
    const other = otherIncome
      .filter((stream) => age >= stream.startAge && age < stream.endAge)
      .reduce((sum, stream) => sum + computeIncomeStreamAmountAtAge(stream, age, config.currentAge, config.incomeGrowthRate), 0);

    let liquidDraw = 0;
    let privatePensionDrawdown = 0;

    if (isRetired) {
      let remainingNeed = Math.max(0, targetSpend - guaranteed - other);

      if (remainingNeed > 0 && canAccessPrivate && currentPensionPot > 0) {
        const safePensionDraw = currentPensionPot * (config.withdrawalRate / 100);
        privatePensionDrawdown = Math.min(remainingNeed, currentPensionPot, safePensionDraw);
        currentPensionPot -= privatePensionDrawdown;
        remainingNeed -= privatePensionDrawdown;
      }

      if (remainingNeed > 0 && currentLiquid > 0) {
        liquidDraw = Math.min(remainingNeed, currentLiquid);
        currentLiquid -= liquidDraw;
      }
    }

    data.push({
      age,
      EmploymentIncome: Math.round(employment),
      LiquidBridgeAssets: Math.round(liquidDraw),
      PrivatePensionDrawdown: Math.round(privatePensionDrawdown),
      DbStatePension: Math.round(guaranteed),
      OtherIncome: Math.round(other),
      TargetSpend: Math.round(targetSpend),
    });

    if (currentLiquid > 0) currentLiquid *= 1 + investmentReturn;
    if (currentPensionPot > 0) currentPensionPot *= 1 + investmentReturn;
  }

  return data;
};
